import { FormControl, FormLabel, Text } from '@chakra-ui/react'
import { Select, SingleValue } from 'chakra-react-select'
import { useMemo } from 'react'
import { CountryCodeData } from '../types/CountryCode'
import { countryCodes } from '../utils/countryCodes'

interface CountryOption {
  label: string
  value: string
  country: CountryCodeData
}

interface Props {
  value?: string
  onChange: (countryCode: string) => void
  isRequired?: boolean
  hideLabel?: boolean
}

export default function PhoneCountrySelect({
  value,
  onChange,
  isRequired = false,
  hideLabel = false,
}: Props) {
  const options: CountryOption[] = useMemo(
    () =>
      countryCodes.map((country) => ({
        label: `${country.flag} ${country.name} (${country.dialCode})`,
        value: country.iso,
        country,
      })),
    []
  )

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value) || null,
    [options, value]
  )

  function handleChange(option: SingleValue<CountryOption>) {
    if (option) {
      onChange(option.value)
    }
  }

  return (
    <FormControl>
      {!hideLabel && (
        <FormLabel>
          Country Code{' '}
          {isRequired && (
            <Text as='span' color='red.500'>
              *
            </Text>
          )}
        </FormLabel>
      )}
      <Select<CountryOption>
        placeholder='Select country'
        options={options}
        value={selectedOption}
        onChange={handleChange}
        isSearchable
        aria-label='Select country code for phone number'
        chakraStyles={{
          container: (provided) => ({
            ...provided,
            minWidth: '200px',
          }),
          control: (provided) => ({
            ...provided,
            minHeight: '40px',
          }),
          menu: (provided) => ({
            ...provided,
            zIndex: 10,
          }),
        }}
        filterOption={(option, searchText) => {
          if (!searchText) return true
          const search = searchText.toLowerCase()
          const country = option.data.country
          return (
            country.name.toLowerCase().includes(search) ||
            country.iso.toLowerCase().includes(search) ||
            country.dialCode.includes(search)
          )
        }}
      />
    </FormControl>
  )
}
